import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { SubmissionService } from '../../submission-data/submission.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

/**
 * Mirrors the legacy `AmendSupersededReturnsController` from
 * `ui/app/components/efile/efileElements/EfileElementsController.js`
 * (line ~4703). Its `vm.createNewReturns()` opens a `confirm-action.html`
 * modal ("Are you sure you want to Create New Amendments/Superseded
 * Returns") via `$uibModal.open` BEFORE calling `JsonObjectFactory.saveJSON`.
 * The migrated screen now reproduces that confirm-then-save flow using
 * the shared `gtw-confirmation-modal` component.
 */
@Component({
    selector: 'gtw-amend-superseded-returns',
    templateUrl: './amend-superseded-returns.component.html',
    styleUrls: ['./amend-superseded-returns.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AmendSupersededReturnsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private subs = new Subscription();

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  mode: 'amend' | 'superseded' = 'amend';
  submitting = false;

  // Confirmation modal state — port of legacy `showConfirmDialog()` which
  // returned a $uibModal promise resolved on Yes / No.
  showConfirm = false;
  confirmConfig: ConfirmationModalConfig = {
    title: 'Amendments/Superseded Returns',
    message: 'Are you sure you want to Create New Amendments Returns ',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Creating return...',
  };

  constructor(
    private http: HttpClient,
    private snackbar: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private submissionService: SubmissionService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.USER_SETTINGS = shell.userSettingsData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    // Prefer SubmissionService.activeSubmission when available — that's the
    // canonical context once a submission tab is active. Fall back to the
    // workspace activeScreen rowData for legacy AngularJS-hosted contexts.
    const activeSubmission: any = this.submissionService?.activeSubmission;
    if (activeSubmission && activeSubmission.SUBMISSION_ID) {
      this.rowData = activeSubmission;
    } else {
      this.rowData = rowPayloadFromActiveScreen(activeScreen) || {};
    }
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  setMode(value: 'amend' | 'superseded'): void {
    this.mode = value;
  }

  /**
   * Opens the confirmation modal — equivalent to legacy `showConfirmDialog()`.
   * The actual save runs on `onConfirm()` only if the user clicks Yes.
   */
  create(): void {
    if (this.submitting) return;
    this.confirmConfig = {
      ...this.confirmConfig,
      message:
        this.mode === 'amend'
          ? 'Are you sure you want to Create New Amendments Returns '
          : 'Are you sure you want to Create New Superseded Returns ',
      loading: false,
    };
    this.showConfirm = true;
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    if (this.submitting) return;
    this.runSave();
  }

  onCancel(): void {
    this.showConfirm = false;
    this.submitting = false;
    this.cdr.markForCheck();
  }

  private runSave(): void {
    this.submitting = true;
    this.confirmConfig = { ...this.confirmConfig, loading: true };
    this.cdr.markForCheck();

    const scenario = this.filterParams?.scenario || this.rowData?.SCENARIO || this.globalService?.scenario || '';
    const taxYear = this.rowData?.TAX_YEAR || this.filterParams?.tax_year || this.globalService?.tax_year || '';
    const ssoId = this.USER_SETTINGS?.user?.sso_id || this.globalService?.sso_id || '';
    const submissionId = this.rowData?.SUBMISSION_ID || '';
    const jcdKey = this.rowData?.JCD_KEY || this.filterParams?.jcd_key || this.globalService?.jcd_key || 250;

    const actionCode = this.mode === 'amend' ? '4hdkul' : 'fyvfhy';
    const url = `${this.GENERAL_CONFIG?.base_url || this.baseURLs.api}/saveJsonObject?action_code=${actionCode}`;

    const body = new URLSearchParams();
    body.set('submission_id', String(submissionId));
    body.set('sso_id', String(ssoId));
    body.set('tax_year', String(taxYear));
    body.set('scenario', String(scenario));
    body.set('jcd_key', String(jcdKey));
    body.set('is_issue_key_enabled', 'N');

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

    this.http.post<any>(url, body.toString(), { headers, withCredentials: true }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.submitting = false;
        this.showConfirm = false;
        this.confirmConfig = { ...this.confirmConfig, loading: false };
        if (res?.callSuccess === '1' && res?.errorMessage === 'null') {
          this.snackbar.openSnackBar(
            this.mode === 'amend'
              ? 'Successfully Created New Amendments Return'
              : 'Successfully Created New Superseded Return',
            'gtw-snackbar--green',
          );
        } else {
          this.snackbar.openSnackBar(res?.errorMessage || 'Unable to create return', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.submitting = false;
        this.showConfirm = false;
        this.confirmConfig = { ...this.confirmConfig, loading: false };
        this.snackbar.openSnackBar('Something went wrong! Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      },
    });
  }
}
